import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '../lib/swal';
import { Loader2, User, Mail, Lock, CreditCard, Phone, ArrowLeft, Home } from 'lucide-react';
import { getMediaUrl } from '@/lib/utils';

const Register = () => {
  const navigate = useNavigate();
  const { register, user, handleGoogleCallback, loading: authLoading } = useAuth();
  const isMountedRef = useRef(true);
  const processingGoogleRef = useRef(false);
  
  const [branding, setBranding] = useState({ logo_url: '/logo.png' });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cedula, setCedula] = useState('');
  const [celular, setCelular] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!authLoading && user && isMountedRef.current) {
      navigate('/usuario', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const processGoogleCallback = async () => {
      if (processingGoogleRef.current) return;
      
      if (window.location.hash.includes('session_id=')) {
        processingGoogleRef.current = true;
        
        if (isMountedRef.current) {
          setLoading(true);
        }
        
        try {
          await handleGoogleCallback();
          if (isMountedRef.current) {
            toast.success('¡Cuenta creada!', 'Tu registro con Google ha sido exitoso');
          }
        } catch (error) {
          if (isMountedRef.current) {
            toast.error('Error', 'No se pudo completar el registro con Google');
            setLoading(false);
          }
        }
      }
    };
    
    processGoogleCallback();
  }, [handleGoogleCallback]);

  const handleRegister = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Error', 'Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('Contraseña corta', 'La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (!isMountedRef.current) return;
    setLoading(true);

    try {
      await register(email, password, name, cedula, celular);
      if (isMountedRef.current) {
        toast.success('¡Registro exitoso!', 'Tu cuenta ha sido creada. Ahora puedes iniciar sesión.');
        setTimeout(() => {
          if (isMountedRef.current) {
            navigate('/login', { replace: true });
          }
        }, 1500);
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error('Error al registrarse', error.response?.data?.detail || 'No se pudo crear la cuenta');
        setLoading(false);
      }
    }
  };

  const fetchBranding = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      const response = await axios.get(`${API}/settings/branding`);
      if (response.data && response.data.logo_url) {
        setBranding({ ...response.data, logo_url: getMediaUrl(response.data.logo_url) });
      }
    } catch (error) {
      console.error('Error al cargar branding:', error);
    }
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  const handleGoogleRegister = () => {
    const redirectUrl = `${window.location.origin}/register`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 px-4 py-8">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600 dark:text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#020202] px-4 py-8 transition-colors duration-500">
      {/* Fondo de la sección */}
      <div className="absolute inset-0 z-0">
        <img src="/assets/fondo2.png" alt="Promo Background" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70"></div>
      </div>
      <div className="w-full max-w-5xl relative z-10 flex flex-col md:flex-row items-center justify-between gap-10 md:gap-16 px-4">
        {/* Columna Izquierda: Logo y Textos */}
        <div className="flex-1 text-center md:text-left">
          <div className="flex justify-center md:justify-start mb-6 md:mb-8">
            <Link to="/" className="relative group inline-block cursor-pointer">
              <img 
                src="/logo.png" 
                alt="WishWay Logo" 
                className="relative h-16 md:h-24 lg:h-32 w-auto object-contain transition-transform duration-500 hover:scale-110 drop-shadow-xl"
              />
            </Link>
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white drop-shadow-md mb-2 md:mb-4 leading-tight tracking-tight">
            Crea tu <br className="hidden md:block" /> cuenta
          </h1>
          <p className="text-gray-300 font-medium text-lg md:text-xl">
            Únete a la mejor plataforma de sorteos
          </p>
        </div>

        {/* Columna Derecha: Formulario */}
        <div className="w-full max-w-md flex-none">

          {/* Card Premium */}
          <Card className="border border-white/10 shadow-[0_30px_60px_rgba(0,0,0,0.8)] bg-black/40 backdrop-blur-xl rounded-[22px]">
          <CardContent className="p-6">
            <form onSubmit={handleRegister} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Name */}
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="name" className="text-sm font-bold text-white/90">Nombre completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#fca050] w-5 h-5" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Juan Pérez"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="pl-10 h-11 border-white/20 bg-white/5 text-white focus:border-[#fca050] focus:ring-[#fca050] transition-all placeholder:text-gray-500 rounded-xl"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="email" className="text-sm font-bold text-white/90">Correo electrónico</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#fca050] w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 h-11 border-white/20 bg-white/5 text-white focus:border-[#fca050] focus:ring-[#fca050] transition-all placeholder:text-gray-500 rounded-xl"
                  />
                </div>
              </div>

              {/* Cedula */}
              <div className="space-y-2">
                <Label htmlFor="cedula" className="text-sm font-bold text-white/90">Cédula</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#fca050] w-5 h-5" />
                  <Input
                    id="cedula"
                    type="text"
                    placeholder="1234567890"
                    value={cedula}
                    onChange={(e) => setCedula(e.target.value)}
                    required
                    className="pl-10 h-11 border-white/20 bg-white/5 text-white focus:border-[#fca050] focus:ring-[#fca050] transition-all placeholder:text-gray-500 rounded-xl"
                  />
                </div>
              </div>

              {/* Celular */}
              <div className="space-y-2">
                <Label htmlFor="celular" className="text-sm font-bold text-white/90">Celular</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#fca050] w-5 h-5" />
                  <Input
                    id="celular"
                    type="tel"
                    placeholder="0912345678"
                    value={celular}
                    onChange={(e) => setCelular(e.target.value)}
                    required
                    className="pl-10 h-11 border-white/20 bg-white/5 text-white focus:border-[#fca050] focus:ring-[#fca050] transition-all placeholder:text-gray-500 rounded-xl"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-bold text-white/90">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#fca050] w-5 h-5" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 h-11 border-white/20 bg-white/5 text-white focus:border-[#fca050] focus:ring-[#fca050] transition-all placeholder:text-gray-500 rounded-xl"
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-bold text-white/90">Confirmar</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#fca050] w-5 h-5" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="pl-10 h-11 border-white/20 bg-white/5 text-white focus:border-[#fca050] focus:ring-[#fca050] transition-all placeholder:text-gray-500 rounded-xl"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="md:col-span-2 w-full py-3 rounded-xl bg-gradient-to-b from-[#fca050] to-[#f24e00] text-[#3f2506] font-black uppercase tracking-widest text-[13px] shadow-[0_6px_0_#b35a00] border-t border-white/40 hover:brightness-110 active:translate-y-1 active:shadow-none transition-all relative overflow-hidden mt-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creando cuenta...
                  </>
                ) : (
                  'Registrarme ahora'
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#0a0a0a] text-gray-400 rounded-full">O crea tu cuenta con</span>
              </div>
            </div>

            {/* Google Login */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 border border-white/20 bg-white/5 hover:bg-white/10 text-white hover:text-white transition-all duration-200 rounded-xl"
              onClick={handleGoogleRegister}
              disabled={loading}
            >
              <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span className="font-medium">Continuar con Google</span>
            </Button>

            {/* Login Link */}
            <div className="mt-5 text-center">
              <p className="text-sm text-gray-400">
                ¿Ya tienes cuenta?{' '}
                <Link 
                  to="/login" 
                  className="font-bold text-[#fca050] hover:text-[#f24e00] transition-colors underline-offset-4 hover:underline"
                >
                  Inicia sesión aquí
                </Link>
              </p>
            </div>

            {/* Back to Home */}
            <div className="mt-4">
              <Link 
                to="/" 
                className="flex items-center justify-center gap-2 w-full py-2.5 border border-white/10 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white transition-all duration-200 rounded-xl text-sm font-medium"
              >
                <Home className="w-4 h-4" /> Volver al inicio
              </Link>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default Register;
